import React, { useState } from "react"
import { useToast } from "~/utils/Toast"
import getAppConfig from "~/utils/getAppConfig"
import { capitalize } from "~/utils/helpers"
import useFetchResource from "~/hooks/useFetchResource"

import { apiSponsorshipAreas } from "~/routes"
import CatchModal from "~/library/CatchModal"
import ConfirmationModal from "~/library/ConfirmationModal"
import AreaForm from "~/components/Sponsorship/AreaForm"
import LoadingWrapper from "~/library/LoadingWrapper"
import type { Area, AreaType } from "~/types/sponsorship/Area"

interface EditAreaProps {
  isOpen: boolean
  onClose: () => void
  areaId: string | null
  onSuccess: (areaType: AreaType) => void
  areaType: AreaType
}

const EditArea: React.FC<EditAreaProps> = ({ isOpen, onClose, areaId, onSuccess, areaType }) => {
  const { venueSlug } = getAppConfig()
  const toast = useToast()
  const [isDeleting, setIsDeleting] = useState(false)

  const { data: sponsorshipArea, isLoading } = useFetchResource<
    { sponsorshipArea: Area },
    "sponsorshipArea"
  >(apiSponsorshipAreas.show.path({ venueSlug, id: areaId }), "sponsorshipArea", {})

  const handleSubmit = (formValues: any) => {
    if (!areaId) return

    const data = {
      venue_slug: venueSlug,
      id: areaId,
      sponsorshipArea: {
        ...formValues,
        areaType,
      },
    }

    const req = apiSponsorshipAreas.update({ data })
    req
      .then((res) => {
        toast("Sponsorship area updated successfully", "success")
        onSuccess(res.sponsorshipArea.areaType)
        onClose()
      })
      .catch((res) => {
        toast(
          `Error updating sponsorship area. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
      })
  }

  const handleDelete = () => {
    setIsDeleting(true)
  }

  const confirmDelete = () => {
    if (!areaId) return

    const data = {
      venue_slug: venueSlug,
      id: areaId,
    }

    const req = apiSponsorshipAreas.destroy({ data })
    req
      .then(() => {
        toast("Sponsorship area deleted successfully", "success")
        setIsDeleting(false)
        onSuccess(areaType)
        onClose()
      })
      .catch((res) => {
        toast(
          `Error deleting sponsorship area. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
        setIsDeleting(false)
      })
  }

  if (!isOpen || !areaId) return null

  return (
    <>
      <CatchModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Edit ${capitalize(areaType)} Sponsorship Area`}
      >
        <LoadingWrapper isLoading={isLoading}>
          <AreaForm
            onSubmit={handleSubmit}
            initialValues={sponsorshipArea}
            isEditing={true}
            onCancel={onClose}
            onDelete={handleDelete}
            areaType={areaType}
            hideAreaType={true}
          />
        </LoadingWrapper>
      </CatchModal>

      <ConfirmationModal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={confirmDelete}
        title={`Delete Area "${sponsorshipArea?.name}"?`}
        description="This action cannot be undone. Are you sure you want to delete this sponsorship area?"
      />
    </>
  )
}

export default EditArea
